import { ReactNode, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Modal,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { AgentList, CompanyList, UsersList } from "../../types/General";
import { USER_TYPE_SLUGS } from "../../utils/enums";

type Props = {
  details: UsersList | CompanyList | AgentList | null;
  type: any;
};

const IMAGE_MEDIA_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/svg"];

const isImage = (mediaType: string) => IMAGE_MEDIA_TYPES.includes(mediaType);

const Documents = ({ details, type }: Props) => {
  const [open, setOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  if (!details) return <Typography>No documents found</Typography>;

  const handleOpen = (doc: any) => {
    if (isImage(doc?.media_type)) {
      setSelectedDoc(doc);
      setOpen(true);
    } else {
      window.open(doc?.media_url);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDoc(null);
  };

  const renderDocuments = (documents: any[], title?: ReactNode) => {
    if (!documents?.length) return null;

    return (
      <Box mb={3}>
        {title && (
          <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
            {title}
          </Typography>
        )}
        {documents.map((doc: any) => (
          <Card
            key={doc.id}
            sx={{
              marginBottom: 2,
              display: "flex",
              alignItems: "center",
              padding: 1,
            }}
          >
            <CardContent
              sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}
            >
              <img
                src={
                  isImage(doc.media_type)
                    ? doc.media_url
                    : "/static/images/docs.png"
                }
                alt={doc.media_name || "Document"}
                style={{
                  width: 90,
                  height: 70,
                  marginRight: 10,
                  objectFit: "contain",
                  border: isImage(doc.media_type)
                    ? "1px solid lightgray"
                    : "none",
                  borderRadius: 10,
                }}
              />
            </CardContent>
            <IconButton onClick={() => handleOpen(doc)}>
              <VisibilityIcon />
            </IconButton>
          </Card>
        ))}
      </Box>
    );
  };

  const hasDocuments =
    type === USER_TYPE_SLUGS.INDIVIDUAL
      ? [
          details?.passport_proof,
          details?.identity_proof,
          details?.contract_proof,
        ].some((docs) => docs?.length > 0)
      : details?.document_proof_1?.length > 0;

  return (
    <Box>
      {type === USER_TYPE_SLUGS.INDIVIDUAL ? (
        <>
          {renderDocuments(details?.passport_proof, "Passport Proof Documents")}
          {renderDocuments(details?.identity_proof, "Identity Proof Documents")}
          {renderDocuments(details?.contract_proof, "Contract Scanned Image")}
        </>
      ) : (
        <>
          {details?.document_name && (
            <>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                {details.document_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Expiry date: {details.document_expiry_date}
              </Typography>
            </>
          )}
          {renderDocuments(details?.document_proof_1)}
        </>
      )}

      {!hasDocuments && <Typography>No documents found</Typography>}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              background: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedDoc && (
            <img
              src={selectedDoc?.media_url || ""}
              alt={selectedDoc?.media_name || ""}
              style={{ maxWidth: "90%", maxHeight: "90%" }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Documents;
